/**
 * React Query hooks for the gateway configuration policy (REQ-GW-014).
 *
 * Like the rest of the panel, the control API is exposed under
 * ``/api/v1/gateway-admin`` (same proxy as applications/keys).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import type {
  EffectivePolicy,
  GatewayPolicy,
  PolicyScope,
  PolicyUpsertPayload,
} from '../types';

const BASE = '/api/v1/gateway-admin';

const keys = {
  effective: (keyId: string, endUser: string) =>
    ['gateway', 'policy', 'effective', keyId, endUser] as const,
  list: (scope: PolicyScope, scopeId: string, endUser: string) =>
    ['gateway', 'policy', 'list', scope, scopeId, endUser] as const,
};

export function useEffectivePolicy(keyId: string | null, endUser?: string) {
  return useQuery({
    queryKey: keys.effective(keyId ?? '', endUser ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<EffectivePolicy>(
        `${BASE}/apigw/v1/policies/effective`,
        { params: { key_id: keyId, end_user: endUser || undefined } },
      );
      return data;
    },
    enabled: !!keyId,
    staleTime: 15 * 1000,
  });
}

export function usePolicies(scope: PolicyScope, scopeId: string, endUser?: string) {
  return useQuery({
    queryKey: keys.list(scope, scopeId, endUser ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<GatewayPolicy[]>(`${BASE}/apigw/v1/policies`, {
        params: { scope, scope_id: scopeId || undefined, end_user: endUser || undefined },
      });
      return data;
    },
    enabled: !!scopeId || !!endUser,
    staleTime: 15 * 1000,
  });
}

export function useUpsertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PolicyUpsertPayload) => {
      const { data } = await apiClient.put<GatewayPolicy>(`${BASE}/apigw/v1/policies`, payload);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gateway', 'policy'] });
    },
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (policyId: string) => {
      await apiClient.delete(`${BASE}/apigw/v1/policies/${policyId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gateway', 'policy'] });
    },
  });
}
